import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    /*
      NOT `payload?.data ?? payload`: an endpoint that legitimately answers
      `{ data: null }` — "no match run yet", "no record" — would fall through to
      the RAW ENVELOPE, handing the caller a truthy `{ data: null }` where it
      expected null. The next `result.shortlist.length` then throws and takes
      the page down. Unwrap on the presence of the key, not the truthiness of
      its value: a null `data` is an answer, not a missing one.
    */
    return payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
  }

  /**
   * As `request`, but for binary responses (PDFs). Returns an object URL the
   * caller can open or download; the caller owns revoking it.
   */
  async requestBlobUrl(endpoint) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });

    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json().catch(() => null) : null;
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }

    return URL.createObjectURL(await res.blob());
  }

  /**
   * As `request`, but returns the full envelope so callers can read `meta`
   * (e.g. pagination totals) alongside `data`.
   */
  async requestWithMeta(endpoint, options) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options?.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
      ...(options?.body ? { body: JSON.stringify(options.body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return { data: payload?.data ?? payload, meta: payload?.meta };
  }

  // ---- Auth ----
  /**
   * The markets this deployment serves. UNAUTHENTICATED — the login screen
   * builds its selector from this before anyone has a token.
   */
  jurisdictions() {
    return this.request('/jurisdictions');
  }

  /**
   * One market's full pack. Unauthenticated, because the anonymous capture
   * form needs its vocabulary before anyone has signed in.
   */
  jurisdictionPack(code) {
    return this.request(`/jurisdictions/${code}`);
  }

  login({ email, password, jurisdiction }) {
    return this.request('/auth/login', {
      method: 'POST',
      body: { email, password, jurisdiction },
    });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users (Admin only) ----
  listUsers({ limit = 50, offset = 0, search, role, isActive, sortBy, sortDir } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.set('search', search);
    if (role) params.set('role', role);
    if (typeof isActive === 'boolean') params.set('isActive', String(isActive));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);

    // Uses the meta-aware variant so the UI can page on `meta.total`.
    return this.requestWithMeta(`/users?${params.toString()}`);
  }

  getUser(id) {
    return this.request(`/users/${id}`);
  }

  createUser({ email, name, password, role }) {
    return this.request('/users', { method: 'POST', body: { email, name, password, role } });
  }

  updateUser(id, changes) {
    return this.request(`/users/${id}`, { method: 'PATCH', body: changes });
  }

  setUserActive(id, isActive) {
    return this.request(`/users/${id}/status`, { method: 'PATCH', body: { isActive } });
  }

  // ---- Land listings ----
  /** Submits a captured parcel. Public — Contacts are anonymous. */
  createListing(payload) {
    return this.request('/listings', { method: 'POST', body: payload });
  }

  listListings({ limit = 50, offset = 0, status, search, guardrail } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    if (guardrail) params.set('guardrail', guardrail);

    return this.requestWithMeta(`/listings?${params.toString()}`);
  }

  getListing(id) {
    return this.request(`/listings/${id}`);
  }

  // --- Listing documents -----------------------------------------------------
  /**
   * Fetches a listing's stored deed through the staff-auth doc-serving route and
   * resolves to an object URL the caller can open in a new tab. `endpoint` is
   * the document's own `url` field from the API (e.g.
   * `/listings/:id/documents/:documentId`). Like the pack PDF, the caller owns
   * revoking the returned object URL.
   */
  getListingDocumentUrl(endpoint) {
    return this.requestBlobUrl(endpoint);
  }

  /** Corrects a captured record; the API re-runs the guardrails. */
  updateListing(id, patch) {
    return this.request(`/listings/${id}`, { method: 'PATCH', body: patch });
  }

  /** Refused by the API while a blocking guardrail is unresolved. */
  promoteListing(id) {
    return this.request(`/listings/${id}/promote`, { method: 'POST' });
  }

  rejectListing(id, reason) {
    return this.request(`/listings/${id}/reject`, { method: 'POST', body: { reason } });
  }

  recordGuardrailAction(id, { code, action, note }) {
    return this.request(`/listings/${id}/guardrail-actions`, {
      method: 'POST',
      body: { code, action, note },
    });
  }

  // ---- Developer mandates & CRM ----
  listMicroMarkets() {
    return this.request('/mandates/micro-markets');
  }

  listDeveloperOrgs(search) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);

    return this.request(`/mandates/orgs?${params.toString()}`);
  }

  createDeveloperOrg({ name, notes }) {
    return this.request('/mandates/orgs', { method: 'POST', body: { name, notes } });
  }

  updateDeveloperOrg(id, patch) {
    return this.request(`/mandates/orgs/${id}`, { method: 'PATCH', body: patch });
  }

  deleteDeveloperOrg(id, { force = false } = {}) {
    return this.request(`/mandates/orgs/${id}?force=${force}`, { method: 'DELETE' });
  }

  listMandates({ orgId, status, search, limit = 100, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (orgId) params.set('orgId', orgId);
    if (status) params.set('status', status);
    if (search) params.set('search', search);

    return this.requestWithMeta(`/mandates?${params.toString()}`);
  }

  /** The mandates matching may run against — expired ones are excluded. */
  matchingPool() {
    return this.request('/mandates/matching-pool');
  }

  createMandate(payload) {
    return this.request('/mandates', { method: 'POST', body: payload });
  }

  updateMandate(id, patch) {
    return this.request(`/mandates/${id}`, { method: 'PATCH', body: patch });
  }

  deleteMandate(id) {
    return this.request(`/mandates/${id}`, { method: 'DELETE' });
  }

  // ---- Location context enrichment ----
  listEnrichmentMarkets() {
    return this.request('/enrichment/markets');
  }

  createEnrichmentMarket(payload) {
    return this.request('/enrichment/markets', { method: 'POST', body: payload });
  }

  updateEnrichmentMarket(id, patch) {
    return this.request(`/enrichment/markets/${id}`, { method: 'PATCH', body: patch });
  }

  /** Refused (400) when Google Places is not connected — nothing is estimated. */
  enrichMarketAmenities(id) {
    return this.request(`/enrichment/markets/${id}/amenities`, { method: 'POST' });
  }

  /**
   * Driving times from this corridor's centroid to every other micro-market's.
   * Refused (400) when Google is not connected — no drive time is invented.
   */
  computeHubTravelTimes(id) {
    return this.request(`/enrichment/markets/${id}/travel-times`, { method: 'POST' });
  }

  /** The stored image behind a filed report's photo, as an object URL. */
  siteVisitPhotoUrl(visitId, photoId) {
    return this.requestBlobUrl(`/enrichment/visits/${visitId}/photos/${photoId}`);
  }

  listSiteVisits({ listingId } = {}) {
    const params = new URLSearchParams();
    if (listingId) params.set('listingId', listingId);

    return this.request(`/enrichment/visits?${params.toString()}`);
  }

  /**
   * Files an onsite field report with its photographs.
   *
   * Multipart, so it bypasses `request` for the same reason the deed upload
   * does: the browser must set the multipart boundary itself.
   */
  async createSiteVisit(fields, photos) {
    const body = new FormData();

    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') body.append(key, value);
    });
    // Zero photos is a real, expected case — the API rejects it, and that
    // rejection is what the analyst needs to see.
    Array.from(photos ?? []).forEach((photo) => body.append('photos', photo));

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}/enrichment/visits`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body,
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const error = new Error(payload?.error?.message || `Submit failed (${res.status})`);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Matching & scoring ----
  listMatchableListings() {
    return this.request('/matching/listings');
  }

  /** Refused (400) for a stale registry unless `overrideReason` is supplied. */
  executeMatchRun(listingId, { weights, overrideReason } = {}) {
    return this.request(`/matching/listings/${listingId}/runs`, {
      method: 'POST',
      body: { ...(weights ? { weights } : {}), ...(overrideReason ? { overrideReason } : {}) },
    });
  }

  latestMatchRun(listingId) {
    return this.request(`/matching/listings/${listingId}/runs`);
  }

  getMatchRun(runId) {
    return this.request(`/matching/runs/${runId}`);
  }

  // ---- Location analysis & colour-coding ----
  /** The decomposable location score + neighbour choropleth for a parcel. */
  getLocationAnalysis(listingId) {
    return this.request(`/analysis/listings/${listingId}`);
  }

  /** Pulls Google Maps amenities + drive-times. Refused (400) when Google is not connected. */
  enrichListing(listingId) {
    return this.request(`/analysis/listings/${listingId}/enrich`, { method: 'POST' });
  }

  // ---- Property packs ----
  listPacks({ listingId } = {}) {
    const params = new URLSearchParams();
    if (listingId) params.set('listingId', listingId);

    return this.request(`/packs?${params.toString()}`);
  }

  /**
   * Drafts a pack. The response carries the QR token's plaintext — this is the
   * ONLY time it is knowable, since the server stores only its hash.
   */
  draftPack({ listingId, mandateId, runId, tier }) {
    return this.request('/packs', {
      method: 'POST',
      body: { listingId, mandateId, ...(runId ? { runId } : {}), ...(tier ? { tier } : {}) },
    });
  }

  getPack(id) {
    return this.request(`/packs/${id}`);
  }

  /** Teaser redaction is applied server-side, so this re-fetches real content. */
  setPackTier(id, tier) {
    return this.request(`/packs/${id}/tier`, { method: 'PUT', body: { tier } });
  }

  /** Saves the edit and returns the citation check for it. */
  editPackCoverLetter(id, body) {
    return this.request(`/packs/${id}/cover-letter`, { method: 'PUT', body: { body } });
  }

  /** Whether the "Ownership & Encumbrance Verified" seal may be applied. */
  /** Renders the current draft as a PREVIEW PDF; resolves to an object URL. */
  packPreviewPdf(id) {
    return this.requestBlobUrl(`/packs/${id}/pdf`);
  }

  /** The stored PDF of a recorded delivery; resolves to an object URL. */
  packDeliveryPdf(id, version) {
    return this.requestBlobUrl(`/packs/${id}/deliveries/${version}/pdf`);
  }

  packStampEligibility(id) {
    return this.request(`/packs/${id}/stamp-eligibility`);
  }

  listPackDeliveries(id) {
    return this.request(`/packs/${id}/deliveries`);
  }

  /** Approve & Deliver. Principal only — the API refuses an Analyst. */
  approvePack(id, { signoffNotes, freshnessStamp }) {
    return this.request(`/packs/${id}/deliveries`, {
      method: 'POST',
      body: { signoffNotes, freshnessStamp: Boolean(freshnessStamp) },
    });
  }

  // ---- Public QR landing (no auth) ----
  /**
   * Resolves a scanned QR token. Deliberately does NOT send the Bearer token:
   * this is the anonymous developer's view, and a stray staff credential would
   * make it impossible to tell whether the endpoint really works logged out.
   */
  async scanPack(token) {
    const res = await fetch(`${API_BASE_URL}/public/packs/${encodeURIComponent(token)}`);
    const payload = await res.json().catch(() => null);

    if (!res.ok) {
      const error = new Error(payload?.error?.message || `This link is not valid.`);
      error.status = res.status;
      throw error;
    }

    return payload?.data ?? payload;
  }

  async requestFromPack(token, body) {
    const res = await fetch(`${API_BASE_URL}/public/packs/${encodeURIComponent(token)}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await res.json().catch(() => null);

    if (!res.ok) {
      const error = new Error(payload?.error?.message || 'That request could not be sent.');
      error.status = res.status;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- QR telemetry (staff) ----
  listScanEvents({ search, deviceType, outcome } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (deviceType) params.set('deviceType', deviceType);
    if (outcome) params.set('outcome', outcome);

    return this.request(`/packs/telemetry/scans?${params.toString()}`);
  }

  listPackRequests({ packId } = {}) {
    const params = new URLSearchParams();
    if (packId) params.set('packId', packId);

    return this.request(`/packs/telemetry/requests?${params.toString()}`);
  }

  // ---- Relationship network ----
  getNetworkGraph({ activeOnly = false } = {}) {
    return this.request(`/network/graph?activeOnly=${activeOnly}`);
  }

  listNetworkOrigins() {
    return this.request('/network/origins');
  }

  listNetworkTargets() {
    return this.request('/network/targets');
  }

  traceNetworkPaths({ originId, targetNodeId, maxHops, activeOnly, minTrust, minConfidence }) {
    return this.request('/network/paths', {
      method: 'POST',
      body: { originId, targetNodeId, maxHops, activeOnly, minTrust, minConfidence },
    });
  }

  inspectNetworkEdge(edgeId) {
    return this.request(`/network/edges/${edgeId}`);
  }

  getNetworkCoverage({ maxHops = 3, activeOnly = true, minTrust = 1, minConfidence = 0.01 } = {}) {
    const query = new URLSearchParams({ maxHops, activeOnly, minTrust, minConfidence });

    return this.request(`/network/coverage?${query}`);
  }

  getNetworkCoverageOrg(orgId, { maxHops = 3, activeOnly = true, minTrust = 1, minConfidence = 0.01 } = {}) {
    const query = new URLSearchParams({ maxHops, activeOnly, minTrust, minConfidence });

    return this.request(`/network/coverage/${orgId}?${query}`);
  }

  linkNetworkNodeOrg(nodeId, orgId) {
    return this.request(`/network/nodes/${nodeId}/link-org`, {
      method: 'POST',
      body: { orgId },
    });
  }

  createNetworkNode(payload) {
    return this.request('/network/nodes', { method: 'POST', body: payload });
  }

  createNetworkEdge(payload) {
    return this.request('/network/edges', { method: 'POST', body: payload });
  }

  // ---- Contact enrichment ----
  // Imports a contact as a person node and, when the enrichment section is
  // configured, fills its third-party title/company + past roles and connects
  // it to its current employer. Answers { node, enrichment }; `enrichment` is
  // null when the section is off (import then behaves exactly as before).
  importNetworkContact(payload) {
    return this.request('/network/contacts/import', { method: 'POST', body: payload });
  }

  // ---- Deal pipeline & financing referrals ----
  getDealBoard() {
    return this.request('/deals/board');
  }

  moveDealStage(id, toStage) {
    return this.request(`/deals/${id}/stage`, { method: 'PATCH', body: { toStage } });
  }

  listFinancingPartners() {
    return this.request('/deals/partners');
  }

  listReferrableDeals() {
    return this.request('/deals/referrable');
  }

  listDealReferrals() {
    return this.request('/deals/referrals');
  }

  draftReferralNarrative({ dealId, partnerId }) {
    return this.request('/deals/referrals/draft', {
      method: 'POST',
      body: { dealId, partnerId },
    });
  }

  dispatchReferral({ dealId, partnerId, narrative, narrativeSource }) {
    return this.request('/deals/referrals', {
      method: 'POST',
      body: { dealId, partnerId, narrative, narrativeSource },
    });
  }

  // ---- Refresh orchestration & freshness ----
  listFreshness({ refreshStatus, entityType, search } = {}) {
    const params = new URLSearchParams();
    if (refreshStatus) params.set('refreshStatus', refreshStatus);
    if (entityType) params.set('entityType', entityType);
    if (search) params.set('search', search);

    return this.request(`/refresh/freshness?${params.toString()}`);
  }

  listRefreshJobs({ status, entityType } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (entityType) params.set('entityType', entityType);

    return this.request(`/refresh/jobs?${params.toString()}`);
  }

  queueRefreshJob({ entityType, entityId, note }) {
    return this.request('/refresh/jobs', {
      method: 'POST',
      body: { entityType, entityId, note },
    });
  }

  /** The pipelines and whether each is really connected — never hard-coded. */
  /** The signed-in overview: pipeline figures and the work waiting. */
  getDashboard() {
    return this.request('/dashboard');
  }

  listRefreshPipelines() {
    return this.request('/refresh/pipelines');
  }

  triggerMasterRefreshRun() {
    return this.request('/refresh/master-run', { method: 'POST' });
  }

  // ---- Registry adjudication ----
  listRegistryWorkItems({ limit = 50, offset = 0, status, search } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.set('status', status);
    if (search) params.set('search', search);

    return this.requestWithMeta(`/registry/work-items?${params.toString()}`);
  }

  getRegistryWorkItem(id) {
    return this.request(`/registry/work-items/${id}`);
  }

  /** Saving the registry record re-runs the fuzzy name match server-side. */
  saveRegistryRecord(id, payload) {
    return this.request(`/registry/work-items/${id}/registry-record`, {
      method: 'PUT',
      body: payload,
    });
  }

  /** Confirming below the match threshold needs an overrideReason. */
  adjudicateWorkItem(id, { decision, overrideReason }) {
    return this.request(`/registry/work-items/${id}/adjudicate`, {
      method: 'POST',
      body: { decision, overrideReason },
    });
  }

  // ---- Conversational capture ----
  listConversations({ limit = 50, offset = 0, search, trustLevel, isActive } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.set('search', search);
    if (trustLevel) params.set('trustLevel', trustLevel);
    if (typeof isActive === 'boolean') params.set('isActive', String(isActive));

    return this.requestWithMeta(`/chat/conversations?${params.toString()}`);
  }

  getConversation(id) {
    return this.request(`/chat/conversations/${id}`);
  }

  sendChatMessage(id, body) {
    return this.request(`/chat/conversations/${id}/messages`, { method: 'POST', body: { body } });
  }

  optOutConversation(id) {
    return this.request(`/chat/conversations/${id}/opt-out`, { method: 'POST' });
  }

  // ---- Runtime provider settings (admin) ----
  listSettings() {
    return this.request('/settings');
  }

  /** `secret` is write-only — omit it to keep the stored one. */
  saveSettings(section, { config, secret, isEnabled }) {
    return this.request(`/settings/${section}`, {
      method: 'PUT',
      body: { config, isEnabled, ...(secret ? { secret } : {}) },
    });
  }

  testSettingsConnection(section) {
    return this.request(`/settings/${section}/test`, { method: 'POST' });
  }

  /** Merges a duplicate profile into this conversation's contact. */
  mergeContact(id, duplicateContactId) {
    return this.request(`/chat/conversations/${id}/merge-contact`, {
      method: 'POST',
      body: { duplicateContactId },
    });
  }

  /**
   * Attaches a deed to a listing.
   *
   * Sent as multipart/form-data, so this bypasses `request` — that helper sets
   * a JSON Content-Type, and the browser must set the multipart boundary itself.
   */
  async uploadListingDocument(listingId, docType, file) {
    const body = new FormData();
    body.append('docType', docType);
    body.append('file', file);

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}/listings/${listingId}/documents`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body,
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const error = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      error.status = res.status;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Copilot ----
  /**
   * Asks the read-only side-drawer copilot. `messages` is the running thread of
   * {role, content} turns (no server-side history in v1). Resolves to
   * {answer, citations, navigations}; a disabled LLM section throws a 400 whose
   * `error.details.code` is 'LLM_NOT_CONNECTED', which the drawer renders as the
   * honest "not connected" state rather than a fake answer.
   */
  copilotAsk(messages) {
    return this.request('/copilot/ask', { method: 'POST', body: { messages } });
  }
}

export default new ApiService();
